import { useState, useEffect, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import {
    initializeUserBookmarks,
    addBookmark,
    removeBookmark,
    updateBookmark,
    updateBookmarkOrder,
    fetchUrlMetadata
} from '../utils/bookmarkUtils';

export const useBookmarks = (userId) => {
    const [bookmarks, setBookmarks] = useLocalStorage('userBookmarks', []);
    const [bookmarkOrder, setBookmarkOrder] = useLocalStorage('userBookmarkOrder', []);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initialize bookmarks
    useEffect(() => {
        const loadBookmarks = async () => {
            if (!userId) return;
            
            // Load from local storage first for immediate display
            if (bookmarks.length > 0 || bookmarkOrder.length > 0) {
                setLoading(false);
            } else {
                setLoading(true);
            }
            
            try {
                const data = await initializeUserBookmarks(userId);
                setBookmarks(data.items || []);
                setBookmarkOrder(data.order || []);
            } catch (err) {
                setError(err.message);
                console.error('Error loading bookmarks:', err);
            } finally {
                setLoading(false);
            }
        };

        loadBookmarks();
    }, [userId]);

    // Add new bookmark
    const handleAddBookmark = useCallback(async (url, title) => {
        try {
            setLoading(true);
            
            // Fetch metadata first
            const metadata = await fetchUrlMetadata(url);
            
            // Create bookmark with metadata
            const newBookmark = await addBookmark(userId, {
                title: title || metadata.title,
                previewImage: metadata.previewImage,
                url
            });

            setBookmarks(prev => [...prev, newBookmark]);
            setBookmarkOrder(prev => [...prev, newBookmark.id]);
            
            return newBookmark;
        } catch (err) {
            setError(err.message);
            console.error('Error adding bookmark:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Remove bookmark
    const handleRemoveBookmark = useCallback(async (bookmarkId) => {
        try {
            // Optimistically update the UI
            setBookmarks(prev => prev.filter(bookmark => bookmark.id !== bookmarkId));
            setBookmarkOrder(prev => prev.filter(id => id !== bookmarkId));

            // Perform the delete operation in the background
            await removeBookmark(userId, bookmarkId);
        } catch (err) {
            setError(err.message);
            console.error('Error removing bookmark:', err);
            // Optional: Revert state if the delete operation fails
            // This would require storing the old state before the optimistic update
            throw err;
        }
    }, [userId]);

    // Update bookmark
    const handleUpdateBookmark = useCallback(async (bookmarkId, updates) => {
        try {
            setLoading(true);
            const updatedBookmark = await updateBookmark(userId, bookmarkId, updates);
            
            setBookmarks(prev => prev.map(bookmark => 
                bookmark.id === bookmarkId ? updatedBookmark : bookmark
            ));
        } catch (err) {
            setError(err.message);
            console.error('Error updating bookmark:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Update bookmark order
    const handleUpdateOrder = useCallback(async (newOrder) => {
        try {
            // Optimistically update local state first
            setBookmarkOrder(newOrder);
            
            // Reorder bookmarks array to match new order
            const orderedBookmarks = newOrder.map(id => 
                bookmarks.find(bookmark => bookmark.id === id)
            ).filter(Boolean);
            
            setBookmarks(orderedBookmarks);
            
            // Then update in Firebase
            await updateBookmarkOrder(userId, newOrder);
        } catch (err) {
            // Revert local state on error
            setBookmarkOrder(bookmarkOrder);
            setBookmarks(bookmarks);
            setError(err.message);
            console.error('Error updating bookmark order:', err);
            throw err;
        }
    }, [userId, bookmarks, bookmarkOrder]);

    // Get ordered bookmarks
    const orderedBookmarks = bookmarkOrder
        .map(id => bookmarks.find(bookmark => bookmark.id === id))
        .filter(Boolean);

    return {
        bookmarks: orderedBookmarks,
        loading,
        error,
        addBookmark: handleAddBookmark,
        removeBookmark: handleRemoveBookmark,
        updateBookmark: handleUpdateBookmark,
        updateOrder: handleUpdateOrder
    };
};
